import { CurrencyCode } from "@/models/currency";

interface ExchangeRates {
    rates: Record<CurrencyCode, number>;
}

/**
 * Converte un prezzo da EUR a un'altra valuta
 * Arrotonda il risultato all'intero più vicino (senza decimali)
 *
 * @param eurPrice - Prezzo in EUR (string o number)
 * @param toCurrency - Valuta di destinazione
 * @param rates - Oggetto con i tassi di cambio
 * @returns Prezzo convertito arrotondato all'intero (string)
 */
export function convertPrice(
    eurPrice: number | string,
    toCurrency: CurrencyCode,
    rates: ExchangeRates["rates"]
): string {
    const numPrice =
        typeof eurPrice === "string" ? parseFloat(eurPrice) : eurPrice;

    if (toCurrency === "EUR") {
        // Arrotonda all'intero più vicino
        return Math.round(numPrice).toString();
    }

    const rate = rates[toCurrency as keyof typeof rates];

    if (!rate || Number.isNaN(numPrice)) {
        // eslint-disable-next-line no-console
        console.warn(
            `Invalid rate or price: rate=${rate}, price=${numPrice}, toCurrency=${toCurrency}, rates=`,
            rates
        );
        // Arrotonda all'intero più vicino anche in caso di errore
        return Math.round(numPrice).toString();
    }

    const converted = numPrice * rate;
    // eslint-disable-next-line no-console
    console.log(
        `convertPrice: ${numPrice} EUR * ${rate} = ${converted} ${toCurrency}`
    );
    // Arrotonda all'intero più vicino
    return Math.round(converted).toString();
}

/**
 * Converte un prezzo da una valuta a EUR (conversione inversa)
 * Arrotonda il risultato all'intero più vicino (senza decimali)
 *
 * @param price - Prezzo nella valuta di origine (string o number)
 * @param fromCurrency - Valuta di origine
 * @param rates - Oggetto con i tassi di cambio
 * @returns Prezzo convertito in EUR arrotondato all'intero (number)
 */
export function convertToEur(
    price: number | string,
    fromCurrency: CurrencyCode,
    rates: ExchangeRates["rates"]
): number {
    const numPrice = typeof price === "string" ? parseFloat(price) : price;

    if (fromCurrency === "EUR") {
        return Math.round(numPrice);
    }

    const rate = rates[fromCurrency as keyof typeof rates];

    if (!rate || Number.isNaN(numPrice) || rate === 0) {
        // eslint-disable-next-line no-console
        console.warn(
            `Invalid rate or price: rate=${rate}, price=${numPrice}, fromCurrency=${fromCurrency}, rates=`,
            rates
        );
        return Math.round(numPrice);
    }

    const converted = numPrice / rate;
    // eslint-disable-next-line no-console
    console.log(
        `convertToEur: ${numPrice} ${fromCurrency} / ${rate} = ${converted} EUR`
    );
    return Math.round(converted);
}

interface EurPrices {
    [key: string]: number | string;
}

/**
 * Applica un valore a un campo nested nell'oggetto target
 * Supporta campi nested (es: "slots.price", "rooms.price")
 */
function applyNestedField(
    target: Record<string, unknown>,
    field: string,
    value: number | string
): void {
    const keys = field.split(".");
    let currentTarget: Record<string, unknown> = target;
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!currentTarget[key]) {
            currentTarget[key] = {};
        }
        currentTarget = currentTarget[key] as Record<string, unknown>;
    }
    currentTarget[keys[keys.length - 1]] = value;
}

/**
 * Applica i prezzi EUR direttamente all'oggetto (senza conversione)
 */
function applyEurPrices<T extends Record<string, unknown>>(
    obj: T,
    eurPrices: EurPrices
): T {
    const converted = { ...obj };
    Object.keys(eurPrices).forEach(field => {
        applyNestedField(converted, field, eurPrices[field]);
    });
    // eslint-disable-next-line no-underscore-dangle
    delete (converted as Record<string, unknown>)._eurPrices;
    return converted;
}

/**
 * Applica i prezzi convertiti all'oggetto
 */
function applyConvertedPrices<T extends Record<string, unknown>>(
    obj: T,
    eurPrices: EurPrices,
    toCurrency: CurrencyCode,
    rates: ExchangeRates["rates"]
): T {
    const converted = { ...obj };
    Object.keys(eurPrices).forEach(field => {
        const eurValue = eurPrices[field];
        const convertedValue = convertPrice(eurValue, toCurrency, rates);
        applyNestedField(converted, field, convertedValue);
    });
    // eslint-disable-next-line no-underscore-dangle
    delete (converted as Record<string, unknown>)._eurPrices;
    return converted;
}

/**
 * Verifica se l'oggetto ha prezzi EUR validi
 */
function hasValidEurPrices(
    obj: Record<string, unknown>
): obj is Record<string, unknown> & { _eurPrices: EurPrices } {
    return (
        // eslint-disable-next-line no-underscore-dangle
        !!obj._eurPrices &&
        // eslint-disable-next-line no-underscore-dangle
        typeof obj._eurPrices === "object"
    );
}

/**
 * Converte tutti i prezzi EUR in un oggetto usando i tassi di cambio
 *
 * L'oggetto deve avere una proprietà `_eurPrices` che contiene i prezzi EUR originali
 * in formato: { "field": eurValue, "nested.field": eurValue, ... }
 *
 * @param obj - Oggetto con prezzi EUR in `_eurPrices`
 * @param toCurrency - Valuta di destinazione
 * @param rates - Tassi di cambio
 * @returns Oggetto con prezzi convertiti (senza `_eurPrices`)
 */
export function convertPriceObject<T extends Record<string, unknown>>(
    obj: T,
    toCurrency: CurrencyCode,
    rates: ExchangeRates["rates"]
): T {
    const objRecord = obj as Record<string, unknown>;

    if (!hasValidEurPrices(objRecord)) {
        return obj;
    }

    // eslint-disable-next-line no-underscore-dangle
    const eurPrices = objRecord._eurPrices as EurPrices;

    if (toCurrency === "EUR") {
        // Se è EUR, usa i prezzi EUR originali direttamente
        return applyEurPrices(obj, eurPrices);
    }

    // Conversione a valuta diversa da EUR
    return applyConvertedPrices(obj, eurPrices, toCurrency, rates);
}

/**
 * Converte un array di oggetti con prezzi EUR
 *
 * @param array - Array di oggetti con `_eurPrices`
 * @param toCurrency - Valuta di destinazione
 * @param rates - Tassi di cambio
 * @returns Array con prezzi convertiti
 */
export function convertPriceArray<T extends Record<string, unknown>>(
    array: T[],
    toCurrency: CurrencyCode,
    rates: ExchangeRates["rates"]
): T[] {
    if (!array || array.length === 0) return array;
    return array.map(item => convertPriceObject(item, toCurrency, rates));
}

interface SlotWithNested {
    tickets?: Array<Record<string, unknown>>;
    rooms?: Array<Record<string, unknown>>;
    [key: string]: unknown;
}

interface TourWithNested {
    rooms?: Array<Record<string, unknown>>;
    slots?: Array<SlotWithNested>;
    [key: string]: unknown;
}

/**
 * Converte un tour completo (con rooms, slots, tickets nested)
 *
 * @param tour - Oggetto tour con `_eurPrices`
 * @param toCurrency - Valuta di destinazione
 * @param rates - Tassi di cambio
 * @returns Tour con tutti i prezzi convertiti
 */
export function convertTour(
    tour: TourWithNested,
    toCurrency: CurrencyCode,
    rates: ExchangeRates["rates"]
): TourWithNested {
    const converted = convertPriceObject(
        tour,
        toCurrency,
        rates
    ) as TourWithNested;

    // Conversione nested per rooms
    if (converted.rooms && Array.isArray(converted.rooms)) {
        converted.rooms = convertPriceArray(converted.rooms, toCurrency, rates);
    }

    // Conversione nested per slots (che possono avere tickets e rooms)
    if (converted.slots && Array.isArray(converted.slots)) {
        converted.slots = converted.slots.map(slot => {
            const convertedSlot = convertPriceObject(
                slot,
                toCurrency,
                rates
            ) as SlotWithNested;

            if (
                convertedSlot?.tickets &&
                Array.isArray(convertedSlot.tickets)
            ) {
                convertedSlot.tickets = convertPriceArray(
                    convertedSlot.tickets,
                    toCurrency,
                    rates
                );
            }
            if (convertedSlot?.rooms && Array.isArray(convertedSlot.rooms)) {
                convertedSlot.rooms = convertPriceArray(
                    convertedSlot.rooms,
                    toCurrency,
                    rates
                );
            }

            return convertedSlot;
        });
    }

    return converted;
}
